import { OneTrustCategory } from '@square/onetrust-compliant-access';
import { useIgnitionLazy } from '@squareup/dex-data-access-oas';
import { CopyLink } from '@squareup/dex-icons/dex/action';
import { CheckmarkCircle } from '@squareup/dex-icons/market/alert';
import { TestProps } from '@squareup/dex-types-shared-utils';
import { useLocalStorage } from '@squareup/dex-ui-hooks';
import {
  useApiExplorerEndpoint,
  useApiExplorerEnvironment,
  useApiExplorerInput,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { ApiExplorerShareApiDialog } from '@squareup/dex-ui-shared-api-explorer-share-api-dialog';
import { Paragraph10 } from '@squareup/dex-ui-shared-base';
import {
  MarketAccessory,
  MarketButton,
  MarketTooltip,
} from '@squareup/dex-ui-shared-market';
import {
  getNextPublicBaseURL,
  isStagingEnvironment,
} from '@squareup/dex-utils-environment';
import { toApiId } from '@squareup/dex-utils-oas';
import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';

import styles from './api-explorer-copy-request-button.module.css';

interface Props {}

const strings = {
  copied: 'Copied',
  copyLink: 'Copy link to request setup',
};

const ApiExplorerCopyRequestButton = React.forwardRef<
  globalThis.HTMLMarketButtonElement,
  PropsWithChildren<Props & TestProps>
>((_, ref) => {
  const endpoint = useApiExplorerEndpoint();
  const [input] = useApiExplorerInput();
  const environment = useApiExplorerEnvironment();

  const ignition = useIgnitionLazy();

  const [showCopyCheckmark, setShowCopyCheckmark] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const [shareModalViewed, setShareModalViewed] = useLocalStorage({
    key: 'api-explorer-share-modal-dismissed',
    category: OneTrustCategory.FUNCTIONAL,
    initialValue: false,
  });

  const shareLink = useMemo(() => {
    if (!endpoint || !ignition) {
      return '';
    }

    const modifiedInput = {
      ...input,
      fileParameters: { filename: input.fileParameters?.file?.name ?? '' },
    };

    const params =
      ignition.ApiExplorerParameterHandler.convertCodegenInputToV1(
        modifiedInput
      );
    const explorerUrl = `${getNextPublicBaseURL()}/explorer/square/${toApiId(
      endpoint.api?.id
    )}/${endpoint.id}`;

    const requestEnvironment = (
      isStagingEnvironment() ? 'staging' : environment.toLowerCase()
    ) as Parameters<typeof ignition.ShareApiHandler.generateLink>[3];

    return ignition.ShareApiHandler.generateLink(
      params,
      explorerUrl,
      endpoint?.squareVersion || '',
      requestEnvironment
    );
  }, [endpoint, input, ignition, environment]);

  const showCopyCheckmarkIconTooltip = () => {
    setShowCopyCheckmark(true);
    setTimeout(() => {
      setShowCopyCheckmark(false);
    }, 2500);
  };

  const onCopyClick = useCallback(async () => {
    if (shareLink) {
      await navigator.clipboard.writeText(shareLink);
      if (!shareModalViewed) {
        setShareModalViewed(true);
        setShowShareModal(true);
      } else {
        showCopyCheckmarkIconTooltip();
      }
    }
  }, [setShareModalViewed, shareLink, shareModalViewed]);

  const onShareModalClose = useCallback(() => {
    setShowShareModal(false);
    showCopyCheckmarkIconTooltip();
  }, []);

  const tooltipText = useMemo(() => {
    if (showCopyCheckmark) {
      return strings.copied;
    }
    return strings.copyLink;
  }, [showCopyCheckmark]);

  return (
    <>
      <MarketTooltip
        popoverDistance={4}
        className={styles['tooltip'] as string}
        disabled={!shareLink}
      >
        <Paragraph10 as="span" slot="content">
          {tooltipText}
        </Paragraph10>
        <MarketButton
          iconOnly
          onClick={onCopyClick}
          ref={ref}
          size="small"
          slot="trigger"
          disabled={!shareLink}
          trackingExtra={JSON.stringify({ endpoint: endpoint?.id })}
          trackingId="share-api-explorer-request-link"
          aria-label={strings.copyLink}
          className={styles['copy-request-btn']}
        >
          <MarketAccessory slot="icon">
            {showCopyCheckmark ? (
              <CheckmarkCircle className={styles['copy-success-icon']} />
            ) : (
              <CopyLink className={styles['copy-link-icon']} />
            )}
          </MarketAccessory>
        </MarketButton>
      </MarketTooltip>
      {showShareModal && (
        <ApiExplorerShareApiDialog onClose={onShareModalClose} />
      )}
    </>
  );
});

ApiExplorerCopyRequestButton.displayName = 'ApiExplorerCopyRequestButton';

export { ApiExplorerCopyRequestButton };
